import {
    Box,
    Stack,
    Typography,
} from "@mui/material";
import { useContext, useEffect, useState, useRef } from "react";
import { AccessContext } from "../../state/AccessProvider";
import ChatThread from "./MuiChatThread";
import { useAppStateContext } from "../../state/AppContextProvider";
import { MuiWelcome } from "./MuiWelcome";
import { MuiQuestionInput } from "../../components/QuestionInput/MuiQuestionInput";
import uuid from "react-uuid";
import { ChatMessage, UploadingStatus } from "../../common/models";
import { AUTHOR_ROLE } from "./utils";
import { Chip } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { SidebarInsertContext, FooterButtonContext } from "../layout/MuiLayout"; // adjust path if needed


export const MuiChat = (): JSX.Element => {
    //Component properties and event handlers
    const appStateContext = useAppStateContext();
    const accessContext = useContext(AccessContext);
    const { sidebarInsertValue, setSidebarInsertValue } = useContext(SidebarInsertContext);
    const { footerButtonValue, setFooterButtonValue } = useContext(FooterButtonContext);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [abortFunction, setAbortFunction] = useState<AbortController | null>(
        null
    );

    const [questionInputValue, setQuestionInputValue] = useState("");
    const [resetInput, setResetInput] = useState<boolean>(false);
    const { glossary } = appStateContext;

    const attachments =
        appStateContext.state.currentConversation.attachments !== undefined
            ? appStateContext.state.currentConversation.attachments
            : [];
    useEffect(() => {
        appStateContext?.getChatHistory();

    }, []);

    //TODO: Implement the following functions
    const stopGenerating = () => {
        abortFunction?.abort();
        setIsLoading(false);
    };

    useEffect(() => {
        if (resetInput) {
            setQuestionInputValue("");
            setResetInput(false);
        }
    }, [resetInput]);

    const messages = appStateContext.state.currentConversation.messages;
    const handleSuggestionClick = async (suggestion: string) => {
        appStateContext.setMaxStep(0); // Reset stepper progress in state

        // Always start completely fresh - clear everything first
        appStateContext.dispatch({ type: "CLEAR_CURRENT_CHAT" });
        appStateContext.dispatch({ type: 'SET_ACTIVE_DOCUMENT', payload: null });
        
        // Wait for state to clear
        await new Promise(resolve => setTimeout(resolve, 10));
        
        // Create a new session with completely empty state
        const sessionId = uuid();
        await appStateContext.dispatch({
            type: 'SET_CURRENT_CONVERSATION',
            payload: {
                sessionId,
                messages: [],
                attachments: [], // Always empty for suggestion clicks
            },
        });
        
        setQuestionInputValue(suggestion);
        setTimeout(() => {
            handleSend(suggestion, sessionId);
        }, 50); // Increased timeout to ensure state is properly set
    };

    const [showFooterButtons, setShowFooterButtons] = useState(true);
    const processedExtraLoginInfo = useRef<string | null>(null);

    const handleSend = async (question: string, id?: string) => {
        try {
            const convSessionId = appStateContext.state.currentConversation.sessionId ?? id;

            let newMessage: ChatMessage = {
                messageId: uuid(),
                content: question,
                authorRole: { label: AUTHOR_ROLE.USER },
                timestamp: new Date().toISOString(),
            };

            // Collect all document IDs from current conversation attachments
            const allDocumentIds = appStateContext.state.currentConversation.attachments
                ?.map(attachment => attachment.documentId)
                .filter(id => id) // Remove any null/undefined IDs
                .join(',') || null;

            var abortContoller = await appStateContext.sendChatRequest(
                convSessionId!,
                newMessage,
                allDocumentIds
            );

            setAbortFunction(abortContoller);
            setResetInput(true); // Clear input after send
            setShowFooterButtons(false); // Hide buttons after send
        } catch (error) {
            console.error("Error sending chat message:", error);
            // Don't clear conversation state on error, just log it
        }
    };

    useEffect(() => {
        if (sidebarInsertValue) {
            setQuestionInputValue(sidebarInsertValue);
            // Optionally trigger send here if you want auto-send:
            handleSend(sidebarInsertValue, appStateContext.state.currentConversation.sessionId ?? undefined);
            setSidebarInsertValue(null); // reset after use
        }
    }, [sidebarInsertValue]);

    useEffect(() => {
        if (footerButtonValue) {
            // Normalize and check if this is a "start new negotiation" button (accept variations)
            const lb = footerButtonValue.toLowerCase().trim();
            const isStartNewNegotiation = lb === "start new negotiation" || lb === "start a new negotiation" || (lb.includes("start") && lb.includes("negotiation"));

            // Check if this is the "Start new negotiation" button that should reset conversation
            if (isStartNewNegotiation) {
                // Start fresh for "Start new negotiation"
                appStateContext.dispatch({ type: "CLEAR_CURRENT_CHAT" });
                appStateContext.dispatch({ type: 'SET_ACTIVE_DOCUMENT', payload: null });
                
                // Wait for state to clear
                setTimeout(async () => {
                    const sessionId = uuid();
                    await appStateContext.dispatch({
                        type: 'SET_CURRENT_CONVERSATION',
                        payload: {
                            sessionId,
                            messages: [],
                            attachments: [],
                        },
                    });
                    
                    setQuestionInputValue(footerButtonValue);
                    appStateContext.dispatch({
                        type: "CLEAR_ORCHSTATE_SUGGESTIONS"
                    });
                    setTimeout(() => {
                        handleSend(footerButtonValue, sessionId);
                        setFooterButtonValue(null); // reset after use
                    }, 10);
                }, 10);
            } else {
                // Continue existing conversation for all other buttons
                setQuestionInputValue(footerButtonValue);
                appStateContext.dispatch({
                    type: "CLEAR_ORCHSTATE_SUGGESTIONS"
                });
                setTimeout(() => {
                    handleSend(footerButtonValue, appStateContext.state.currentConversation.sessionId ?? undefined);
                    setFooterButtonValue(null); // reset after use
                }, 0);
            }
        }
    }, [footerButtonValue]);

    // Show buttons again when orchestration state updates
    useEffect(() => {
        setShowFooterButtons(true);
    }, [appStateContext.state.currentOrchState]);

    // Salesforce SSO: auto-load or start chat based on extraLoginInfo
    useEffect(() => {
        const info = accessContext.extraLoginInfo;
        if (!info || info.type !== "salesforceUser") return;

        // Create a unique key for this extraLoginInfo to prevent duplicate processing
        const infoKey = `${info.taskId}-${info.conversationId}`;
        if (processedExtraLoginInfo.current === infoKey) return;
        
        processedExtraLoginInfo.current = infoKey;

        // If taskId and NO conversationId: start new chat
        if (info.taskId && !info.conversationId) {
            appStateContext.dispatch({ type: "CLEAR_CURRENT_CHAT" });
            appStateContext.dispatch({ type: "SET_ACTIVE_DOCUMENT", payload: null });
            handleSuggestionClick("I'd like help with an upcoming negotiation.");
        }

        // If both taskId and conversationId: load chat from history
        if (info.taskId && info.conversationId) {
            
            // Use getConversationBySessionId directly instead of getChatHistory
            appStateContext.getConversationBySessionId(info.conversationId!)
                .then(() => {
                    console.log("Successfully loaded Salesforce conversation");
                })
                .catch((error) => {
                    console.error("Failed to load Salesforce conversation:", error);
                    // Only clear if we don't have any ongoing conversation
                    if (appStateContext.state.currentConversation.messages.length === 0) {
                        appStateContext.dispatch({ type: "CLEAR_CURRENT_CHAT" });
                    }
                });
        }
    }, [
        accessContext.extraLoginInfo
    ]);

    return (
        <>
             {/* Main Content */}
            <Box id="chat-area"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    width: "99%",
                    maxWidth: { xs: "100%", sm: "720px", md: "840px" },
                    mx: "auto",
                    boxSizing: "border-box",
                }}
            >
                {/* Welcome Section */}
                {messages.length === 0 && <MuiWelcome onSuggestionClick={handleSuggestionClick} />}

                {/* Chat Thread */}
                {messages.length > 0 && (
                    <ChatThread showFooterButtons={showFooterButtons} retry={handleSend} />
                )}
                
                {/* Chat Document Context */}
                {([UploadingStatus.ANALYZING, UploadingStatus.UPLOADING].includes(appStateContext.state.uploadingStatus)) && (
                    <Chip
                        sx={{
                            maxWidth: 'calc(100% - 200px)',
                            alignSelf: 'center',
                        }}
                        color='secondary'
                        icon={<CircularProgress size={15} />}
                        label={`${UploadingStatus.UPLOADING ? `Uploading` : 'Analyzing'} Document`}
                    />
                )}

                {/* Input Section */}
                {messages.length > 0 && (
                    <Stack
                        spacing={2}
                        sx={{
                            // mt: 8, 
                            width: "100%",
                            backgroundColor: "background.main",
                            position: 'sticky',
                            bottom: '0px',
                            marginBottom: '20px',
                            justifyContent: 'flex-end',
                            height: 'fit-content',
                            flex: 1,
                            zIndex: 11,
                            bgcolor: 'background.default',
                        }}
                    >
                        {/* Read-Only Banner */}
                        {appStateContext.state.isReadOnlyMode && (
                            <Box
                                sx={{
                                    border: '2px solid #e50c20',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    textAlign: 'center',
                                    bgcolor: 'background.paper',
                                    mb: 2,
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#e50c20',
                                        fontWeight: 600,
                                    }}
                                >
                                    This is a shared Conversation; you can read but not interact
                                </Typography>
                            </Box>
                        )}

                        {/* Question Input - Hidden in read-only mode */}
                        {!appStateContext.state.isReadOnlyMode && (
                            <MuiQuestionInput
                                clearOnSend
                                disabled={isLoading}
                                onSend={handleSend}
                                initialValue={questionInputValue || ""}
                                resetInput={resetInput}
                            />
                        )}
                    </Stack>
                )}
            </Box>
        </>
    );
};
